/**
 * Instalação, atualização e estado offline.
 *
 * Três coisas que o navegador expõe de forma desajeitada e que a interface
 * precisa de forma simples: dá para instalar, existe versão nova, estou online.
 */

import { useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Registro do service worker
// ---------------------------------------------------------------------------

/** Chamado quando um worker novo já está pronto e só espera a troca. */
type AoAtualizar = (aplicar: () => void) => void;

let registro: ServiceWorkerRegistration | null = null;

export function registrarServiceWorker(aoAtualizar?: AoAtualizar): void {
  if (!('serviceWorker' in navigator)) return;
  // Em desenvolvimento o Vite serve os módulos direto; um worker cacheando por
  // cima disso só produziria confusão sobre qual código está rodando.
  if (import.meta.env.DEV) return;

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((reg) => {
        registro = reg;

        // Já havia um worker esperando quando a página abriu.
        if (reg.waiting && navigator.serviceWorker.controller) {
          aoAtualizar?.(() => trocarPara(reg.waiting));
        }

        reg.addEventListener('updatefound', () => {
          const novo = reg.installing;
          if (!novo) return;
          novo.addEventListener('statechange', () => {
            // `controller` existente significa que isto é uma atualização, não a
            // primeira instalação — só aí faz sentido avisar o usuário.
            if (novo.state === 'installed' && navigator.serviceWorker.controller) {
              aoAtualizar?.(() => trocarPara(novo));
            }
          });
        });
      })
      .catch(() => {
        /* Sem service worker o app continua funcionando, só não offline. */
      });

    let recarregando = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (recarregando) return;
      recarregando = true;
      window.location.reload();
    });
  });
}

function trocarPara(worker: ServiceWorker | null): void {
  worker?.postMessage({ type: 'SKIP_WAITING' });
}

/**
 * Apaga as respostas de API guardadas.
 *
 * Chamado no logout: sem isto, quem abrisse o app offline neste aparelho veria
 * as sessões e os tempos do usuário anterior.
 */
export function limparCacheDaApi(): void {
  navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_API_CACHE' });
  // Também direto, caso não haja worker controlando a página neste momento.
  if ('caches' in window) void caches.delete('mychrono-api-v1');
}

export function verificarAtualizacao(): void {
  void registro?.update();
}

// ---------------------------------------------------------------------------
// App Android
// ---------------------------------------------------------------------------

/**
 * Onde o APK fica para baixar.
 *
 * Servido pelo Caddy direto de uma pasta do servidor, fora do container: trocar
 * o APK não pede rebuild do app, e o arquivo de ~10 MB não entra no precache do
 * service worker de quem só usa a versão web.
 */
export const APK_URL = '/download/mychronohelper.apk';

/**
 * Navegador de Android, fora do app nativo.
 *
 * É onde vale oferecer o APK: o app nativo é o único jeito de puxar do MyChron
 * pelo WiFi, porque o navegador não abre o socket TCP que o logger fala.
 */
export function ehAndroidNoNavegador(): boolean {
  const cap = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  if (cap?.isNativePlatform?.()) return false;
  return /android/i.test(navigator.userAgent);
}

// ---------------------------------------------------------------------------
// Instalação
// ---------------------------------------------------------------------------

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let promptGuardado: BeforeInstallPromptEvent | null = null;
const ouvintes = new Set<() => void>();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Sem isto o Chrome mostra a própria barra de instalação, que aparece em
    // cima do conteúdo e não combina com nada do app.
    event.preventDefault();
    promptGuardado = event as BeforeInstallPromptEvent;
    ouvintes.forEach((f) => f());
  });

  window.addEventListener('appinstalled', () => {
    promptGuardado = null;
    ouvintes.forEach((f) => f());
  });
}

export function estaInstalado(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari no iOS não implementa display-mode e usa esta propriedade própria.
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function ehIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPadOS moderno se apresenta como Mac; a distinção é o suporte a toque.
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

export interface EstadoInstalacao {
  /** O navegador ofereceu o prompt nativo e ele ainda não foi usado. */
  podeInstalar: boolean;
  /** Rodando como app instalado. */
  instalado: boolean;
  /**
   * iOS não tem API de instalação: o caminho é Compartilhar → Adicionar à Tela
   * de Início, e a interface precisa dizer isso em vez de oferecer um botão que
   * não existe.
   */
  precisaInstrucoesIos: boolean;
}

export function useInstalacao(): EstadoInstalacao & { instalar: () => Promise<boolean> } {
  const [, forcar] = useState(0);

  useEffect(() => {
    const f = () => forcar((n) => n + 1);
    ouvintes.add(f);
    return () => {
      ouvintes.delete(f);
    };
  }, []);

  const instalado = estaInstalado();

  return {
    podeInstalar: promptGuardado !== null && !instalado,
    instalado,
    precisaInstrucoesIos: ehIos() && !instalado && promptGuardado === null,
    instalar: async () => {
      if (!promptGuardado) return false;
      await promptGuardado.prompt();
      const { outcome } = await promptGuardado.userChoice;
      promptGuardado = null;
      ouvintes.forEach((f) => f());
      return outcome === 'accepted';
    },
  };
}

// ---------------------------------------------------------------------------
// Conexão
// ---------------------------------------------------------------------------

export function useOnline(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const subiu = () => setOnline(true);
    const caiu = () => setOnline(false);
    window.addEventListener('online', subiu);
    window.addEventListener('offline', caiu);
    return () => {
      window.removeEventListener('online', subiu);
      window.removeEventListener('offline', caiu);
    };
  }, []);

  return online;
}
